import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../../services/ApiService';

// Predefined categories for dance-related products
const PREDEFINED_CATEGORIES = [
  'Shoes',
  'Outfits',
  'Accessories',
  'Classes',
  'Health',
  'Equipment',
];

const GoodsSection = ({ userId, userDances = [], initialTab = 'my' }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab); // 'my' | 'saved'
  const [goods, setGoods] = useState([]);
  const [savedGoods, setSavedGoods] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [editingGood, setEditingGood] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    link: '',
    description: '',
    category: '',
    customCategory: '',
    danceStyles: [],
  });
  const [availableDanceStyles, setAvailableDanceStyles] = useState([]);
  const [showAllStyles, setShowAllStyles] = useState(false);
  const [isAmazonLink, setIsAmazonLink] = useState(false);
  const [originalLink, setOriginalLink] = useState('');
  const [privateOnly, setPrivateOnly] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Sort
  const [sortOrder, setSortOrder] = useState('recent');
  const [showSortMessage, setShowSortMessage] = useState(false);

  const toggleSort = () => {
    const newOrder = sortOrder === 'recent' ? 'oldest' : 'recent';
    setSortOrder(newOrder);
    setShowSortMessage(true);
    setTimeout(() => setShowSortMessage(false), 1500);
  };

  const sortItems = (items) => {
    return [...items].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortOrder === 'recent' ? dateB - dateA : dateA - dateB;
    });
  };

  const renderSortButton = () => (
    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
      {showSortMessage && (
        <span style={{ fontSize: '0.8rem', color: '#6B7280', animation: 'fadeIn 0.2s ease-out' }}>
          {sortOrder === 'recent' ? 'Most recent' : 'Oldest first'}
        </span>
      )}
      <button
        onClick={toggleSort}
        title={sortOrder === 'recent' ? 'Showing most recent' : 'Showing oldest first'}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
          color: '#9CA3AF', display: 'flex', alignItems: 'center', transition: 'color 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
        onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'}
      >
        <i className={`bi ${sortOrder === 'recent' ? 'bi-sort-down' : 'bi-sort-up'}`} style={{ fontSize: '1.1rem' }}></i>
      </button>
    </div>
  );

  useEffect(() => {
    if (activeTab === 'my') {
      fetchGoods();
    }
  }, [userId, selectedCategory, activeTab]);

  const fetchSavedGoods = useCallback(async () => {
    setSavedLoading(true);
    try {
      const response = await ApiService.get('goods/saved');
      if (response.data?.success) {
        setSavedGoods(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching saved goods:', error);
      setSavedGoods([]);
    } finally {
      setSavedLoading(false);
    }
  }, []);

  // Fetch saved goods on mount (for badge count) and when switching to saved tab
  useEffect(() => {
    fetchSavedGoods();
  }, [fetchSavedGoods]);

  useEffect(() => {
    if (activeTab === 'saved') {
      fetchSavedGoods();
    }
  }, [activeTab, fetchSavedGoods]);

  const handleUnsave = async (goodId) => {
    try {
      await ApiService.post(`goods/community/${goodId}/like`);
      setSavedGoods(prev => prev.filter(g => g._id !== goodId));
    } catch (error) {
      console.error('Error unsaving good:', error);
    }
  };

  useEffect(() => {
    const fetchDanceStyles = async () => {
      try {
        const response = await ApiService.get('goods/dance-styles');
        if (response.data?.success) {
          setAvailableDanceStyles(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching dance styles:', error);
      }
    };
    fetchDanceStyles();
  }, []);

  const fetchGoods = async () => {
    setLoading(true);
    try {
      let url = 'goods/my-goods';
      if (selectedCategory) {
        url += `?category=${encodeURIComponent(selectedCategory)}`;
      }
      const response = await ApiService.get(url);
      if (response.data?.success) {
        setGoods(response.data.data || []);
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching goods:', error);
      setGoods([]);
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setModalMode('add');
    setFormData({ title: '', link: '', description: '', category: '', customCategory: '', danceStyles: [] });
    setFormError('');
    setShowAllStyles(false);
    setIsAmazonLink(false);
    setOriginalLink('');
    setPrivateOnly(false);
    setEditingGood(null);
    setShowModal(true);
  };

  const openEditModal = (good) => {
    setModalMode('edit');
    const isPredefined = PREDEFINED_CATEGORIES.includes(good.category);
    setFormData({
      title: good.title,
      link: good.link,
      description: good.description || '',
      category: isPredefined ? good.category : 'custom',
      customCategory: isPredefined ? '' : good.category,
      danceStyles: good.danceStyles || [],
    });
    setFormError('');
    setShowAllStyles(false);
    setEditingGood(good);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingGood(null);
    setFormData({ title: '', link: '', description: '', category: '', customCategory: '', danceStyles: [] });
    setFormError('');
    setShowAllStyles(false);
    setIsAmazonLink(false);
    setOriginalLink('');
    setPrivateOnly(false);
  };

  const toggleDanceStyle = (style) => {
    setFormData(prev => ({
      ...prev,
      danceStyles: prev.danceStyles.includes(style)
        ? prev.danceStyles.filter(s => s !== style)
        : [...prev.danceStyles, style],
    }));
  };

  const checkAmazon = (url) => {
    try {
      let fullUrl = url;
      if (!url.match(/^https?:\/\//i)) fullUrl = 'https://' + url;
      return /\bamazon\b/.test(new URL(fullUrl).hostname.toLowerCase());
    } catch {
      return false;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormError('');

    // Detect Amazon links in real time
    if (name === 'link') {
      const amazon = value.trim().length > 5 && checkAmazon(value);
      setIsAmazonLink(amazon);
      if (!amazon) {
        setOriginalLink('');
        setPrivateOnly(false);
      }
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setFormError('Title is required');
      return false;
    }
    if (formData.title.length > 100) {
      setFormError('Title cannot exceed 100 characters');
      return false;
    }
    if (!formData.link.trim()) {
      setFormError('Link is required');
      return false;
    }
    if (formData.description.length > 250) {
      setFormError('Description cannot exceed 250 characters (~50 words)');
      return false;
    }
    const category = formData.category === 'custom' ? formData.customCategory : formData.category;
    if (!category.trim()) {
      setFormError('Category is required');
      return false;
    }
    if (isAmazonLink && !privateOnly && !originalLink.trim()) {
      setFormError('Please provide the original product link from the brand\'s website, or check "Save to my profile only".');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    setFormError('');

    const category = formData.category === 'custom' ? formData.customCategory.trim() : formData.category;

    try {
      if (modalMode === 'add') {
        const payload = {
          title: formData.title.trim(),
          link: formData.link.trim(),
          description: formData.description.trim(),
          category,
          danceStyles: formData.danceStyles,
        };
        if (isAmazonLink) {
          payload.originalLink = originalLink.trim();
          payload.privateOnly = privateOnly;
        }
        const response = await ApiService.post('goods', payload);

        if (response.data?.success) {
          closeModal();
          fetchGoods();
        } else {
          setFormError(response.data?.error || 'Failed to add recommendation');
        }
      } else {
        const response = await ApiService.put(`goods/${editingGood._id}`, {
          title: formData.title.trim(),
          description: formData.description.trim(),
          category,
          danceStyles: formData.danceStyles,
        });

        if (response.data?.success) {
          closeModal();
          fetchGoods();
        } else {
          setFormError(response.data?.error || 'Failed to update recommendation');
        }
      }
    } catch (error) {
      console.error('Error saving good:', error);
      setFormError(error.response?.data?.error || 'An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (goodId) => {
    setDeleting(true);
    try {
      const response = await ApiService.delete(`goods/${goodId}`);
      if (response.data?.success) {
        setDeleteConfirm(null);
        fetchGoods();
      }
    } catch (error) {
      console.error('Error deleting good:', error);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Shoes': 'bi-fire',
      'Outfits': 'bi-stars',
      'Accessories': 'bi-suit-diamond-fill',
      'Classes': 'bi-lightning-charge-fill',
      'Health': 'bi-shield-fill-check',
      'Equipment': 'bi-speaker-fill',
    };
    return icons[category] || 'bi-box-seam';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Shoes': '#A65474',
      'Outfits': '#6B3A5B',
      'Accessories': '#E8C547',
      'Classes': '#54A0FF',
      'Health': '#8CB9A3',
      'Equipment': '#FF9F43',
    };
    return colors[category] || '#9B59B6';
  };

  const wordCount = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  if (loading && activeTab === 'my') {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="spinner"></div>
        <p>Loading your recommendations...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>My Dance Goods</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
            A library of goods that elevate your dance experience
          </p>
        </div>
        {activeTab === 'my' && (
          <button
            onClick={openAddModal}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.6rem 1rem',
              background: 'linear-gradient(135deg, #6B3A5B, #A65474)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            <i className="bi bi-plus-lg"></i>
            Add Recommendation
          </button>
        )}
      </div>

      {/* Tab Toggle */}
      <div style={{
        display: 'flex',
        gap: '0',
        marginBottom: '1.5rem',
        borderBottom: '2px solid #e5e7eb',
      }}>
        <button
          onClick={() => setActiveTab('my')}
          style={{
            padding: '0.6rem 1.25rem',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'my' ? '2px solid #6B3A5B' : '2px solid transparent',
            marginBottom: '-2px',
            color: activeTab === 'my' ? '#6B3A5B' : '#6b7280',
            fontWeight: activeTab === 'my' ? '600' : '400',
            fontSize: '0.9rem',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          <i className="bi bi-box-seam" style={{ marginRight: '0.4rem' }}></i>
          My Recommendations
        </button>
        <button
          onClick={() => setActiveTab('saved')}
          style={{
            padding: '0.6rem 1.25rem',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'saved' ? '2px solid #6B3A5B' : '2px solid transparent',
            marginBottom: '-2px',
            color: activeTab === 'saved' ? '#6B3A5B' : '#6b7280',
            fontWeight: activeTab === 'saved' ? '600' : '400',
            fontSize: '0.9rem',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          <i className="bi bi-heart" style={{ marginRight: '0.4rem' }}></i>
          Saved
          {savedGoods.length > 0 && (
            <span style={{
              marginLeft: '0.4rem',
              background: '#6B3A5B',
              color: 'white',
              borderRadius: '10px',
              padding: '0.1rem 0.45rem',
              fontSize: '0.72rem',
              fontWeight: '600',
            }}>
              {savedGoods.length}
            </span>
          )}
        </button>
      </div>

      {/* ===== MY RECOMMENDATIONS TAB ===== */}
      {activeTab === 'my' && (<>

      {/* Category Filter */}
      {categories.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontSize: '0.9rem',
              minWidth: '180px',
            }}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      )}


      {/* Goods Grid */}
      {goods.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#6b7280' }}>
          <i className="bi bi-bag-heart" style={{ fontSize: '2.5rem', color: '#A65474', display: 'block', marginBottom: '1rem' }}></i>
          <h3 style={{ margin: '0 0 0.5rem', color: '#374151' }}>No Recommendations Yet</h3>
          <p style={{ margin: 0 }}>
            {selectedCategory
              ? `You haven't added any items in "${selectedCategory}" yet.`
              : 'Start building your library of dance essentials!'}
          </p>
          <button
            onClick={openAddModal}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: '#6B3A5B',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem',
            }}
          >
            Add Your First Recommendation
          </button>
        </div>
      ) : (
        <>
        {renderSortButton()}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1rem',
        }}>
          {sortItems(goods).map((good) => (
            <div
              key={good._id}
              style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                overflow: 'hidden',
                transition: 'box-shadow 0.2s',
              }}
            >
              {/* Card Header */}
              <div style={{
                padding: '1rem',
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'flex-start',
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: getCategoryColor(good.category),
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.1rem',
                  flexShrink: 0,
                }}>
                  <i className={`bi ${getCategoryIcon(good.category)}`}></i>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h4 style={{
                    margin: 0,
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    color: '#374151',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {good.title}
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.35rem' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.15rem 0.5rem',
                      background: `${getCategoryColor(good.category)}15`,
                      color: getCategoryColor(good.category),
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                    }}>
                      {good.category}
                    </span>
                    {good.danceStyles?.slice(0, 2).map(style => (
                      <span key={style} style={{
                        display: 'inline-block',
                        padding: '0.15rem 0.5rem',
                        background: '#f3f0f5',
                        color: '#6B3A5B',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                      }}>
                        {style}
                      </span>
                    ))}
                    {good.danceStyles?.length > 2 && (
                      <span style={{ fontSize: '0.7rem', color: '#9ca3af', alignSelf: 'center' }}>
                        +{good.danceStyles.length - 2}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Card Body - Link */}
              <div style={{ padding: '0 1rem' }}>
                <a
                  href={good.link.startsWith('http') ? good.link : `https://${good.link}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: '#3b82f6',
                    fontSize: '0.85rem',
                    textDecoration: 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <i className="bi bi-link-45deg"></i>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{good.link}</span>
                </a>
              </div>

              {/* Expandable Description */}
              {good.description && (
                <div style={{ padding: '0.75rem 1rem 0' }}>
                  <button
                    onClick={() => setExpandedId(expandedId === good._id ? null : good._id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: '#6b7280',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                    }}
                  >
                    <i className={`bi bi-chevron-${expandedId === good._id ? 'up' : 'down'}`}></i>
                    {expandedId === good._id ? 'Hide' : 'Show'} description
                  </button>
                  {expandedId === good._id && (
                    <p style={{
                      margin: '0.5rem 0 0',
                      fontSize: '0.85rem',
                      color: '#4b5563',
                      lineHeight: '1.5',
                    }}>
                      {good.description}
                    </p>
                  )}
                </div>
              )}

              {/* Card Footer */}
              <div style={{
                padding: '0.75rem 1rem',
                marginTop: '0.5rem',
                borderTop: '1px solid #f3f4f6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                  Added {formatDate(good.createdAt)}
                </span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => openEditModal(good)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '0.25rem',
                      color: '#6b7280',
                      cursor: 'pointer',
                      fontSize: '1rem',
                    }}
                    title="Edit"
                  >
                    <i className="bi bi-pencil"></i>
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(good)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '0.25rem',
                      color: '#ef4444',
                      cursor: 'pointer',
                      fontSize: '1rem',
                    }}
                    title="Remove"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      </>)}

      {/* ===== SAVED TAB ===== */}
      {activeTab === 'saved' && (
        savedLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div className="spinner"></div>
            <p>Loading saved goods...</p>
          </div>
        ) : savedGoods.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#6b7280' }}>
            <i className="bi bi-heart" style={{ fontSize: '2.5rem', color: '#A65474', display: 'block', marginBottom: '1rem' }}></i>
            <h3 style={{ margin: '0 0 0.5rem', color: '#374151' }}>No Saved Goods Yet</h3>
            <p style={{ margin: '0 0 1rem' }}>
              Products you save from the community will appear here for easy access.
            </p>
            <button
              onClick={() => navigate('/goods')}
              style={{
                padding: '0.6rem 1.25rem',
                background: 'linear-gradient(135deg, #6B3A5B, #A65474)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              <i className="bi bi-bag-heart" style={{ marginRight: '0.4rem' }}></i>
              Browse Community Goods
            </button>
          </div>
        ) : (
          <>
          {renderSortButton()}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1rem',
          }}>
            {sortItems(savedGoods).map((good) => (
              <div
                key={good._id}
                style={{
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  transition: 'box-shadow 0.2s',
                }}
              >
                <div style={{
                  padding: '1rem',
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'flex-start',
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: getCategoryColor(good.categories?.[0] || 'General'),
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.1rem',
                    flexShrink: 0,
                  }}>
                    <i className={`bi ${getCategoryIcon(good.categories?.[0] || 'General')}`}></i>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h4 style={{
                      margin: 0,
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      color: '#374151',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {good.title}
                    </h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.35rem' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.15rem 0.5rem',
                        background: `${getCategoryColor(good.categories?.[0] || 'General')}15`,
                        color: getCategoryColor(good.categories?.[0] || 'General'),
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                      }}>
                        {good.categories?.[0] || 'General'}
                      </span>
                      {good.brand && (
                        <span style={{
                          display: 'inline-block',
                          padding: '0.15rem 0.5rem',
                          background: '#f3f4f6',
                          color: '#6b7280',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                        }}>
                          {good.brand}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Social + Actions */}
                <div style={{
                  padding: '0.5rem 1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#6B3A5B',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                }}>
                  <i className="bi bi-people-fill"></i>
                  <span>{good.recommendationCount || 0} recommendation{(good.recommendationCount || 0) !== 1 ? 's' : ''}</span>
                </div>

                {/* Card Footer */}
                <div style={{
                  padding: '0.6rem 1rem',
                  borderTop: '1px solid #f3f4f6',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <a
                    href={good.link?.startsWith('http') ? good.link : `https://${good.link}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      color: '#3b82f6',
                      fontSize: '0.82rem',
                      textDecoration: 'none',
                    }}
                  >
                    <i className="bi bi-box-arrow-up-right"></i>
                    View Product
                  </a>
                  <button
                    onClick={() => handleUnsave(good._id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '0.25rem 0.5rem',
                      color: '#A65474',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                    }}
                    title="Remove from saved"
                  >
                    <i className="bi bi-heart-fill"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
          </>
        )
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeModal}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '1rem 1.25rem',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
                {modalMode === 'add' ? 'Add Recommendation' : 'Edit Recommendation'}
              </h3>
              <button
                onClick={closeModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.25rem',
                  color: '#6b7280',
                  cursor: 'pointer',
                }}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '1.25rem' }}>
              {formError && (
                <div style={{
                  padding: '0.75rem',
                  background: '#fee2e2',
                  border: '1px solid #fca5a5',
                  borderRadius: '6px',
                  color: '#dc2626',
                  fontSize: '0.85rem',
                  marginBottom: '1rem',
                }}>
                  {formError}
                </div>
              )}

              {/* Title */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', marginBottom: '0.35rem' }}>
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Capezio Dance Shoes"
                  maxLength={100}
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                  }}
                />
                <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                  {formData.title.length}/100 characters
                </span>
              </div>

              {/* Link */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', marginBottom: '0.35rem' }}>
                  Product Link *
                </label>
                <input
                  type="text"
                  name="link"
                  value={formData.link}
                  onChange={handleInputChange}
                  placeholder="https://example.com/product"
                  disabled={modalMode === 'edit'}
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    background: modalMode === 'edit' ? '#f9fafb' : 'white',
                  }}
                />
                {modalMode === 'edit' && (
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                    Link cannot be changed after creation
                  </span>
                )}
              </div>

              {/* Amazon Detection */}
              {isAmazonLink && modalMode === 'add' && (
                <div style={{
                  padding: '0.85rem',
                  background: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                }}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <i className="bi bi-info-circle-fill" style={{ color: '#d97706', fontSize: '1rem', marginTop: '0.1rem' }}></i>
                    <div>
                      <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '500', color: '#92400e' }}>
                        We support smaller businesses and responsible consumerism
                      </p>
                      <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#a16207' }}>
                        Amazon links are not shared with the community. Please provide the original product link from the brand's website.
                      </p>
                    </div>
                  </div>

                  {!privateOnly && (
                    <div style={{ marginTop: '0.6rem' }}>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', marginBottom: '0.35rem', color: '#92400e' }}>
                        Original Product Link *
                      </label>
                      <input
                        type="text"
                        value={originalLink}
                        onChange={(e) => { setOriginalLink(e.target.value); setFormError(''); }}
                        placeholder="https://brand-website.com/product"
                        style={{
                          width: '100%',
                          padding: '0.6rem 0.75rem',
                          border: '1px solid #fde68a',
                          borderRadius: '6px',
                          fontSize: '0.9rem',
                          background: 'white',
                        }}
                      />
                    </div>
                  )}

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginTop: '0.6rem',
                    fontSize: '0.82rem',
                    color: '#92400e',
                    cursor: 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={privateOnly}
                      onChange={(e) => { setPrivateOnly(e.target.checked); setFormError(''); }}
                      style={{ accentColor: '#6B3A5B' }}
                    />
                    Save to my profile only (don't share with community)
                  </label>
                </div>
              )}

              {/* Category */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', marginBottom: '0.35rem' }}>
                  Category *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                  }}
                >
                  <option value="">Select a category</option>
                  {PREDEFINED_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                  <option value="custom">+ Add custom category</option>
                </select>
              </div>

              {/* Custom Category Input */}
              {formData.category === 'custom' && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', marginBottom: '0.35rem' }}>
                    Custom Category *
                  </label>
                  <input
                    type="text"
                    name="customCategory"
                    value={formData.customCategory}
                    onChange={handleInputChange}
                    placeholder="Enter your category"
                    maxLength={50}
                    style={{
                      width: '100%',
                      padding: '0.6rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                    }}
                  />
                </div>
              )}

              {/* Dance Styles */}
              {availableDanceStyles.length > 0 && (() => {
                // Build ordered list: "All Styles" first, then user's practiced dances, then the rest
                const userPracticedStyles = userDances
                  .map(d => d.style)
                  .filter(s => s && availableDanceStyles.includes(s));
                const orderedStyles = [
                  'All Styles',
                  ...userPracticedStyles.filter(s => s !== 'All Styles'),
                  ...availableDanceStyles.filter(s => s !== 'All Styles' && !userPracticedStyles.includes(s)),
                ];
                const initialCount = 1 + userPracticedStyles.filter(s => s !== 'All Styles').length + Math.max(0, 6 - userPracticedStyles.length);

                return (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', marginBottom: '0.35rem' }}>
                    Dance Styles (optional)
                  </label>
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                    Tag the dance styles this product is best for
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                    {(showAllStyles ? orderedStyles : orderedStyles.slice(0, initialCount)).map(style => {
                      const isSelected = formData.danceStyles.includes(style);
                      return (
                        <button
                          key={style}
                          type="button"
                          onClick={() => toggleDanceStyle(style)}
                          style={{
                            padding: '0.3rem 0.6rem',
                            borderRadius: '20px',
                            border: isSelected ? '1.5px solid #6B3A5B' : '1px solid #d1d5db',
                            background: isSelected ? '#6B3A5B' : 'white',
                            color: isSelected ? 'white' : '#374151',
                            fontSize: '0.78rem',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            fontWeight: isSelected ? '500' : '400',
                          }}
                        >
                          {style}
                        </button>
                      );
                    })}
                    {orderedStyles.length > initialCount && (
                      <button
                        type="button"
                        onClick={() => setShowAllStyles(!showAllStyles)}
                        style={{
                          padding: '0.3rem 0.6rem',
                          borderRadius: '20px',
                          border: '1px dashed #9ca3af',
                          background: 'transparent',
                          color: '#6b7280',
                          fontSize: '0.78rem',
                          cursor: 'pointer',
                        }}
                      >
                        {showAllStyles ? 'Show less' : `+${orderedStyles.length - initialCount} more`}
                      </button>
                    )}
                  </div>
                  {formData.danceStyles.length > 0 && (
                    <span style={{ fontSize: '0.75rem', color: '#6B3A5B', marginTop: '0.35rem', display: 'block' }}>
                      {formData.danceStyles.length} style{formData.danceStyles.length !== 1 ? 's' : ''} selected
                    </span>
                  )}
                </div>
                );
              })()}

              {/* Description */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', marginBottom: '0.35rem' }}>
                  Description (optional)
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Why do you recommend this product? (~50 words max)"
                  rows={3}
                  maxLength={250}
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    resize: 'vertical',
                  }}
                />
                <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                  {wordCount(formData.description)}/~50 words ({formData.description.length}/250 chars)
                </span>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={submitting}
                  style={{
                    padding: '0.6rem 1.25rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    background: 'white',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: '0.6rem 1.25rem',
                    background: submitting ? '#9ca3af' : 'linear-gradient(135deg, #6B3A5B, #A65474)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                  }}
                >
                  {submitting ? 'Saving...' : modalMode === 'add' ? 'Add My Item' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => !deleting && setDeleteConfirm(null)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '400px',
              padding: '1.5rem',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>Remove Recommendation?</h3>
            <p style={{ margin: '0 0 1.5rem', color: '#6b7280', fontSize: '0.9rem' }}>
              "{deleteConfirm.title}" will be removed from your profile. The recommendation will still
              be visible in the community collection, and you will keep your community point.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                style={{
                  padding: '0.6rem 1.25rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: 'white',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm._id)}
                disabled={deleting}
                style={{
                  padding: '0.6rem 1.25rem',
                  background: deleting ? '#9ca3af' : '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                }}
              >
                {deleting ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoodsSection;
